// client/src/components/admin/AdminMonitoringDigest.js
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import PageLayout from '../common/layout/PageLayout'
import * as monitoringApi from '../../api/monitoring'
import InfoTooltip from '../common/InfoTooltip'
import HelpModal from '../common/HelpModal'
import DigestHelpContent from './DigestHelpContent'
import './AdminMonitoring.css'

function AdminMonitoringDigest() {
  const navigate = useNavigate()
  const { isAdmin, loading: authLoading } = useAuth()

  const [digest, setDigest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [period, setPeriod] = useState('24h')

  const loadDigest = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Calculate date range based on period
      const now = new Date()
      let startDate = null

      switch (period) {
        case '1h':
          startDate = new Date(now.getTime() - 60 * 60 * 1000)
          break
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
          break
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          break
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          break
        default:
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      }

      const digestData = await monitoringApi.generateDigest(
        startDate.toISOString(),
        now.toISOString(),
      )
      setDigest(digestData.digest || digestData)
    } catch (err) {
      console.error('Error loading digest:', err)
      setError('Failed to load monitoring digest. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    if (!authLoading && isAdmin) {
      loadDigest()
    }
  }, [authLoading, isAdmin, loadDigest])

  if (authLoading) {
    return (
      <PageLayout>
        <div className="loading-spinner">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="error-message">
          <h3>Access Denied</h3>
          <p>You must be an administrator to view this page.</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <div className="admin-monitoring-digest">
        <div className="monitoring-header">
          <div>
            <h1>Monitoring Digest</h1>
            <p className="text-muted">System health summary and trends</p>
          </div>
          <div className="monitoring-actions">
            <button
              className="btn btn-sm btn-outline-primary"
              data-bs-toggle="modal"
              data-bs-target="#digestHelpModal"
              aria-label="Open digest help"
            >
              <span aria-hidden="true">?</span> Help
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigate('/admin/monitoring')}
            >
              ← Back to Monitoring
            </button>
          </div>
        </div>

        {/* Period Selector */}
        <div className="mb-4">
          <label htmlFor="period-select" className="form-label">
            Time Period
            <InfoTooltip content="Select the time range for aggregated metrics. Longer periods show trends, shorter periods show recent details." />
          </label>
          <select
            id="period-select"
            className="form-select form-select-sm"
            style={{ maxWidth: '200px' }}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading-spinner">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading digest...</span>
            </div>
          </div>
        ) : digest ? (
          <div className="digest-content">
            {/* Summary Stats */}
            <div className="row mb-4">
              <div className="col-12 col-md-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="card-title">
                      {digest.summary?.total_incidents || 0}
                    </h3>
                    <p className="card-text text-muted">
                      Total Incidents
                      <InfoTooltip content="Total number of incidents detected during the selected period, regardless of severity." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="card-title">
                      {digest.summary?.critical_incidents || 0}
                    </h3>
                    <p className="card-text text-danger">
                      Critical
                      <InfoTooltip content="Number of critical-severity incidents requiring immediate attention." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="card-title">
                      {digest.summary?.warning_incidents || 0}
                    </h3>
                    <p className="card-text text-warning">
                      Warnings
                      <InfoTooltip content="Number of warning-level incidents indicating metrics approaching thresholds." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="card-title">
                      {digest.sla?.uptime_percent != null
                        ? Number(digest.sla.uptime_percent).toFixed(2)
                        : 'N/A'}
                      %
                    </h3>
                    <p className="card-text text-muted">
                      Avg Uptime
                      <InfoTooltip content="Average system uptime percentage. SLA target is 99.9%." />
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* SLA Metrics */}
            {digest.sla && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5>
                    SLA Performance
                    <InfoTooltip content="Service Level Agreement metrics showing performance against targets." />
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-12 col-md-4">
                      <h6>
                        Average Response Time
                        <InfoTooltip content="Mean API response time across all requests. Target: <500ms." />
                      </h6>
                      <p className="text-muted">
                        {digest.sla.avg_response_time_ms != null
                          ? Number(digest.sla.avg_response_time_ms).toFixed(2)
                          : 'N/A'}{' '}
                        ms
                      </p>
                    </div>
                    <div className="col-12 col-md-4">
                      <h6>
                        Error Rate
                        <InfoTooltip content="Percentage of failed requests over total requests. Target: <1%." />
                      </h6>
                      <p className="text-muted">
                        {digest.sla.error_rate_percent != null
                          ? Number(digest.sla.error_rate_percent).toFixed(2)
                          : 'N/A'}
                        %
                      </p>
                    </div>
                    <div className="col-12 col-md-4">
                      <h6>
                        Total Requests
                        <InfoTooltip content="Total number of API requests processed during the selected period." />
                      </h6>
                      <p className="text-muted">
                        {digest.sla.total_requests?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Performance Metrics */}
            {digest.metrics && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5>
                    Performance Metrics
                    <InfoTooltip content="Aggregated performance data showing latency distribution and resource usage." />
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {digest.metrics.latency && (
                      <>
                        <div className="col-12 col-md-3">
                          <h6>
                            P50 Latency
                            <InfoTooltip content="Median latency - 50% of requests completed faster than this time." />
                          </h6>
                          <p className="text-muted">
                            {Number(digest.metrics.latency.p50).toFixed(2)} ms
                          </p>
                        </div>
                        <div className="col-12 col-md-3">
                          <h6>
                            P95 Latency
                            <InfoTooltip content="95th percentile - only 5% of requests were slower than this." />
                          </h6>
                          <p className="text-muted">
                            {Number(digest.metrics.latency.p95).toFixed(2)} ms
                          </p>
                        </div>
                        <div className="col-12 col-md-3">
                          <h6>
                            P99 Latency
                            <InfoTooltip content="99th percentile - indicates worst-case performance for most users." />
                          </h6>
                          <p className="text-muted">
                            {Number(digest.metrics.latency.p99).toFixed(2)} ms
                          </p>
                        </div>
                      </>
                    )}
                    {digest.metrics.db_pool && (
                      <div className="col-12 col-md-3">
                        <h6>
                          DB Pool Usage
                          <InfoTooltip content="Average database connection pool usage. Warning threshold: >70%." />
                        </h6>
                        <p className="text-muted">
                          {Number(digest.metrics.db_pool.usage_percent).toFixed(
                            1,
                          )}
                          %
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Incidents */}
            {digest.incidents && digest.incidents.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h5>
                    Incidents During Period
                    <InfoTooltip content="All incidents detected during the selected time period, showing severity and resolution status." />
                  </h5>
                </div>
                <div className="card-body">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>
                          Severity
                          <InfoTooltip content="Critical requires immediate attention, Warning should be monitored" />
                        </th>
                        <th>Type</th>
                        <th>Message</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {digest.incidents.map((incident, idx) => (
                        <tr key={idx}>
                          <td className="text-muted small">
                            {new Date(incident.created_at).toLocaleString()}
                          </td>
                          <td>
                            <span
                              className={`badge bg-${incident.severity === 'critical' ? 'danger' : 'warning'}`}
                            >
                              {incident.severity}
                            </span>
                          </td>
                          <td>{incident.type}</td>
                          <td>{incident.message}</td>
                          <td>
                            {incident.metric_value != null
                              ? Number(incident.metric_value).toFixed(2)
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="alert alert-info">
            <p>No digest data available for the selected period.</p>
          </div>
        )}

        {/* Help Modal */}
        <HelpModal
          id="digestHelpModal"
          title="Monitoring Digest Help"
          size="lg"
        >
          <DigestHelpContent />
        </HelpModal>
      </div>
    </PageLayout>
  )
}

export default AdminMonitoringDigest
